import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ActivityIndicator,
    FlatList,
    Image,
    Keyboard,
    KeyboardAvoidingView,
    Platform,
    Pressable,
    RefreshControl,
    StyleSheet,
    TouchableOpacity,
    useWindowDimensions,
    View,
} from "react-native";
import { ThemeConsumer } from "@rneui/themed";
import { shallowEqual } from "react-redux";

import CommonHeader from "../../../shared/commonComponents/commonHeader";
import { ListToolbar, LocationItem } from "../../../shared/commonComponents/listToolbar";
import {
    ZenBadge,
    ZenEmptyComponent,
    ZenFilterModal,
    ZenIcon,
    ZenLoader,
    ZenText,
} from "zen-core-elements";
import { filterItemsProps } from "zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal";

import { useAppDispatch, useAppSelector, getScreenPermission } from "../../../redux/hooks";
import {
    clearInventoryCache,
    getAllPosInventoryProducts,
    getAllPosProductSettings,
} from "../../../redux/slices/inventorySlice";
import { getAllLocationList } from "../../../redux/slices/locationSlice";
import { config } from "../../../shared/environment";
import UpdateQuantity from "./updateQty";
import { CustomModal } from "../../../shared/commonComponents/customModal";
import { getAllProductSettingsResponse, ProductRow } from "./inventory.model";
import { SortProps } from "../productList/productModule";
import { appColors, appFonts, fontSize } from "../../../themes/appTheme";

const LIMIT = 10;

const sortOptionsData: SortProps[] = [
    { id: 1, name: "Product name A-Z", field: "name", order: "ASC" },
    { id: 2, name: "Product name Z-A", field: "name", order: "DESC" },
];

const ProductInventory: FC = () => {
    const { width, height } = useWindowDimensions();
    const isLandscape = width > 900;
    const dispatch = useAppDispatch();

    // Permission check
    const { isWrite: isInventoryWrite = true } =
        useAppSelector(getScreenPermission("InventoryStack"), shallowEqual) || {};

    // Select settings and locations from Redux to prevent redundant API calls
    const reduxProductSettings = useAppSelector(
        (state) => state?.inventory?.getProductSettings?.value?.response
    );
    const storeLocations = useAppSelector(
        (state) =>
            state?.common?.getStoreLocations?.value?.length
                ? state?.common?.getStoreLocations?.value
                : state?.location?.locationData?.value?.storeLocation
    );

    // Data states
    const [inventoryProducts, setInventoryProducts] = useState<ProductRow[]>([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState<number>(0);

    // Loaders
    const [load, setLoad] = useState<boolean>(true);
    const [refresh, setRefresh] = useState<boolean>(false);
    const [isFooterLoading, setIsFooterLoading] = useState<boolean>(false);
    const onMomentumScrollBegin = useRef(false);

    const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)

    const productSettings = reduxProductSettings;
    const [isLowStock, setIsLowStock] = useState<boolean>(false);

    // Search, Filter & Sort
    const [searchText, setSearchText] = useState<string | null>(null);
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    const [sortOrder, setSortOrder] = useState<{ field: string; order: string; id: number }>({
        field: "name",
        order: "ASC",
        id: 1,
    });
    const [isVisibleFilter, setIsVisibleFilter] = useState<boolean>(false);
    const [filtercount, setFilterCount] = useState<number>(0);
    const [filterValue, setFilterValue] = useState<{ stockCount: number | null } | null>(null);
    const [filterData, setFilterData] = useState<filterItemsProps[]>([
        {
            id: "1",
            filterName: "Stock Count",
            filterType: "dropdown",
            initialSelected: "",
            filterValue: [
                { label: "0", value: "0" },
                { label: "1-10", value: "1-10" },
                { label: "11-20", value: "11-20" },
                { label: "21-30", value: "21-30" },
                { label: "31-40", value: "31-40" },
                { label: "41-50", value: "41-50" },
                { label: "51-60", value: "51-60" },
                { label: "61-70", value: "61-70" },
                { label: "71-80", value: "71-80" },
                { label: "81-90", value: "81-90" },
                { label: "91-100", value: "91-100" },
                { label: "Above 100", value: "above 100" },
                // { label: "Unlimited", value: "Unlimited" },
                { label: "Not Available", value: "NotAvailable" },
            ],
        },
    ]);

    // Update Quantity Modal
    const [isUpdatingQuantity, setIsUpdatingQuantity] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<ProductRow | null>(null);
    const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchText?.trim() ?? "");
        }, 500);
        return () => clearTimeout(timer);
    }, [searchText]);

    // Fetch product settings and locations only if not already cached in Redux
    useEffect(() => {
        if (!reduxProductSettings) {
            dispatch(getAllPosProductSettings(config?.storeId));
        }

    }, [reduxProductSettings, dispatch]);

    // Main fetch function with Redux API caching support
    const isRefreshing = useRef(false);

    const fetchInventoryProducts = useCallback(
        async (
            targetOffset: number,
            bypassCache: boolean = false,
            overrideSearch?: string,
            overrideFilter?: any,
            overrideSort?: { field: string; order: string; id: number },
            overrideLowStock?: boolean
        ) => {
            const currentSearch =
                overrideSearch !== undefined ? overrideSearch : debouncedSearch;
            const currentFilter =
                overrideFilter !== undefined
                    ? overrideFilter
                    : filterValue?.stockCount || null;
            const currentSort =
                overrideSort !== undefined ? overrideSort : sortOrder;
            const currentLowStock =
                overrideLowStock !== undefined ? overrideLowStock : isLowStock;

            const payload = {
                offset: targetOffset,
                limit: LIMIT,
                locationId: locationId ?? null,
                value: currentSearch,
                filterData: currentFilter,
                storeId: config?.storeId,
                type: currentSort?.order ?? "ASC",
                field: "name",
                lowStock: currentLowStock ? 1 : 0,
            };

            try {
                const response = await dispatch(
                    getAllPosInventoryProducts({
                        storeId: config?.storeId,
                        data: payload,
                        refresh: bypassCache,
                    })
                ).unwrap();

                if (response) {
                    setTotal(Number(response?.products?.count || 0));
                    const rows = response?.products?.rows || [];
                    if (targetOffset > 0) {
                        setInventoryProducts((prev) => [...prev, ...rows]);
                    } else {
                        setInventoryProducts(rows);
                    }
                }
            } catch (err: any) {
                console.log("Error in fetchInventoryProducts", err?.response?.data || err?.message || err);
            } finally {
                setLoad(false);
                setRefresh(false);
                setIsFooterLoading(false);
            }
        },
        [debouncedSearch, filterValue, sortOrder, isLowStock, dispatch, config?.storeId]
    );

    useEffect(() => {

        if (isRefreshing.current) {
            return;
        }
        setLoad(true);
        setOffset(0);
        fetchInventoryProducts(0, false);
    }, [debouncedSearch, filterValue, sortOrder, isLowStock, fetchInventoryProducts]);

    // Handle pull to refresh: resets search, sort, and filter to initial state (bypasses cache)
    const handleRefresh = useCallback(() => {
        setRefresh(true);
        isRefreshing.current = true;
        dispatch(clearInventoryCache());
        setSearchText(null);
        setDebouncedSearch("");
        setSortOrder({ field: "name", order: "ASC", id: 1 });
        setIsLowStock(false);
        setFilterCount(0);
        setFilterValue(null);
        setFilterData((prev) =>
            prev.map((item) => ({ ...item, initialSelected: "" }))
        );
        setOffset(0);

        fetchInventoryProducts(
            0,
            true,
            "",
            null,
            { field: "name", order: "ASC", id: 1 },
            false
        ).finally(() => {
            isRefreshing.current = false;
        });
    }, [dispatch, fetchInventoryProducts]);

    // Handle pagination on end reached
    const handleEndReached = () => {
        if (!onMomentumScrollBegin.current) return;
        if (isFooterLoading || load) return;
        if (total > offset + LIMIT) {
            setIsFooterLoading(true);
            const nextOffset = offset + LIMIT;
            setOffset(nextOffset);
            fetchInventoryProducts(nextOffset, false);
        }
    };


    // Render single inventory item card
    const renderInventoryCard = useCallback(
        ({ item, index }: { item: ProductRow; index: number }) => {
            const variantStock = item?.multiLocationVariantStocks?.[0];
            const stockValue =
                variantStock?.stock != null && !isNaN(Number(variantStock.stock))
                    ? Number(variantStock.stock)
                    : null;

            // Determine stock status and badge styling
            let statusText = "Not Available";
            let statusBadgeBg = "#F1F5F9";
            let statusBadgeText = "#64748B";

            if (variantStock != null) {
                if (stockValue === 0) {
                    statusText = "Out of stock";
                    statusBadgeBg = "#FEEAEA";
                    statusBadgeText = appColors.red;
                } else {
                    statusText = "Available";
                    statusBadgeBg = "#E8F8F0";
                    statusBadgeText = appColors.activeColor;
                }
            }

            // Low stock calculation
            const isThreshold = productSettings?.isThresholdEnabled;
            const thresholdVal = variantStock?.lowStockThresholdValue
                ? Number(variantStock.lowStockThresholdValue)
                : null;
            const isItemLowStock =
                isThreshold &&
                stockValue !== null &&
                stockValue > 0 &&
                thresholdVal !== null &&
                stockValue <= thresholdVal;

            const hasImageError = imageErrors[item?.id];
            const imageUri =
                !hasImageError && item?.product?.featured_image
                    ? config.AWSImageUrl + item.product.featured_image
                    : null;

            return (
                <ThemeConsumer>
                    {({ theme }) => (
                        <View style={[styles.card, isLandscape && styles.gridCard]}>
                            {/* Card Header: Product Name + Status */}
                            <View style={styles.cardHeader}>
                                <View style={styles.titleWrapper}>
                                    <ZenText
                                        content={item?.product?.name || "Unnamed Product"}
                                        bold
                                        numberOfLines={1}
                                        size={theme?.mediumFontSize ?? fontSize.mediumFontSize}
                                        color={theme?.dark ?? appColors.dark}
                                        style={styles.productTitle}
                                    />
                                    {item?.product?.isDigitalProduct && (
                                        <ZenBadge
                                            badgeName="Digital"
                                            badgeColor="#FDF2F8"
                                            badgeTextColor="#DB2777"
                                            badgeContainerStyle={styles.digitalBadge}
                                            badgeTextStyle={styles.badgeText}
                                        />
                                    )}
                                </View>
                                <ZenBadge
                                    badgeName={statusText}
                                    badgeColor={statusBadgeBg}
                                    badgeTextColor={statusBadgeText}
                                    badgeContainerStyle={styles.statusBadge}
                                    badgeTextStyle={styles.badgeText}
                                />
                            </View>

                            <View style={styles.divider} />

                            {/* Card Body: Thumbnail, Info, and Action */}
                            <View style={styles.cardBody}>
                                {/* Thumbnail */}
                                <Image
                                    source={imageUri ? { uri: imageUri } : require("../../../assets/noimage.jpg")}
                                    style={styles.thumbnail}
                                    resizeMode="cover"
                                    onError={() =>
                                        setImageErrors((prev) => ({
                                            ...prev,
                                            [item?.id]: true,
                                        }))
                                    }
                                />

                                {/* Product details */}
                                <View style={styles.infoCol}>
                                    {item?.variantName && !item?.isDefault ? (
                                        <View style={styles.metaRow}>
                                            <ZenText
                                                content="Variant: "
                                                size={theme?.smallFontSize ?? fontSize.smallFontSize}
                                                color={theme?.labelColor ?? appColors.labelColor}
                                                style={styles.metaLabel}
                                            />
                                            <ZenText
                                                content={item.variantName}
                                                bold
                                                size={theme?.smallFontSize ?? fontSize.smallFontSize}
                                                color={theme?.dark ?? appColors.dark}
                                                numberOfLines={1}
                                            />
                                        </View>
                                    ) : null}

                                    <View style={styles.metaRow}>
                                        <ZenText
                                            content="SKU: "
                                            size={theme?.smallFontSize ?? fontSize.smallFontSize}
                                            color={theme?.labelColor ?? appColors.labelColor}
                                            style={styles.metaLabel}
                                        />
                                        <ZenText
                                            content={item?.sku ? item.sku : "N/A"}
                                            bold
                                            size={theme?.smallFontSize ?? fontSize.smallFontSize}
                                            color={theme?.lightDark ?? appColors.lightDark}
                                        />
                                    </View>

                                    <View style={styles.metaRow}>
                                        <ZenText
                                            content="Stock: "
                                            size={theme?.smallFontSize ?? fontSize.smallFontSize}
                                            color={theme?.labelColor ?? appColors.labelColor}
                                            style={styles.metaLabel}
                                        />
                                        {stockValue === 0 ? (
                                            <ZenText
                                                content="0 (Out of stock)"
                                                bold
                                                size={theme?.smallFontSize ?? fontSize.smallFontSize}
                                                color={appColors.red}
                                            />
                                        ) : stockValue !== null ? (
                                            <View style={styles.stockRow}>
                                                <ZenText
                                                    content={String(stockValue)}
                                                    bold
                                                    size={theme?.smallFontSize ?? fontSize.smallFontSize}
                                                    color={theme?.dark ?? appColors.dark}
                                                />
                                                {isItemLowStock && (
                                                    <View style={styles.lowStockPill}>
                                                        <ZenIcon
                                                            name="alert-circle-outline"
                                                            type="material-community"
                                                            size={14}
                                                            color={appColors.pendingColor}
                                                        />
                                                        <ZenText
                                                            content="Low"
                                                            bold
                                                            size={theme?.tinyFontSize ?? fontSize.badgeFontSize}
                                                            color={appColors.pendingColor}
                                                        />
                                                    </View>
                                                )}
                                            </View>
                                        ) : (
                                            <ZenText
                                                content="N/A"
                                                bold
                                                size={theme?.smallFontSize ?? fontSize.smallFontSize}
                                                color={theme?.lightDark ?? appColors.lightDark}
                                            />
                                        )}
                                    </View>
                                </View>

                                {/* Action: Edit Quantity */}
                                {isInventoryWrite && (
                                    <TouchableOpacity
                                        activeOpacity={0.7}
                                        style={styles.editButton}
                                        onPress={() => {
                                            setSelectedItem(item);
                                            setIsUpdatingQuantity(true);
                                        }}
                                        accessibilityLabel="Update Stock Quantity"
                                    >
                                        <ZenIcon
                                            name="pencil-outline"
                                            type="material-community"
                                            size={20}
                                            color={theme?.primary ?? appColors.primary}
                                        />
                                    </TouchableOpacity>
                                )}
                            </View>
                        </View>
                    )}
                </ThemeConsumer>
            );
        },
        [productSettings, imageErrors, isInventoryWrite, isLandscape]
    );

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView
                    onStartShouldSetResponder={() => {
                        Keyboard?.dismiss();
                        return false;
                    }}
                    behavior={Platform.OS === "ios" ? "padding" : undefined}
                    style={styles.screenContainer}
                >
                    {/* Header */}
                    <CommonHeader
                        headerName={isLowStock ? "Low Stock Products" : "Inventory"}
                        backIcon={isLowStock}
                        backIconOnpress={() => setIsLowStock(false)}
                    />

                    {/* Integrated ListToolbar */}
                    <ListToolbar
                        search={{
                            value: searchText ?? "",
                            onChange: (val) => setSearchText(typeof val === "function" ? val(searchText ?? "") : val),
                            onCancel: () => setSearchText(null),
                            placeholder: "Search inventory products...",
                        }}
                        filter={{
                            count: filtercount,
                            onPress: () => setIsVisibleFilter(true),
                        }}
                        sort={{
                            selectedId: sortOrder?.id ?? 1,
                            onSelect: (selectedSort) =>
                                setSortOrder(selectedSort as { field: string; order: string; id: number }),
                            title: "Sort by",
                            options: sortOptionsData,
                        }}
                        // location={{
                        //     label: defaultLoc || "All Location",
                        //     locationList: dropdownLocations,
                        //     onSelect: handleLocationSelect,
                        // }}
                        buttons={
                            productSettings?.isThresholdEnabled
                                ? [
                                    {
                                        title: isLowStock ? "All Stock" : "Low Stock",
                                        variant: isLowStock ? "primary" : "outline",
                                        onPress: () => setIsLowStock((prev) => !prev),
                                    },
                                ]
                                : []
                        }
                    />

                    {/* Content List */}
                    {load ? (
                        <View style={styles.loaderContainer}>
                            <ZenLoader lottieType="Local" lottie={require("../../../assets/infinityLoader.json")} />
                        </View>
                    ) : (
                        <FlatList
                            key={isLandscape ? "grid-2-col" : "list-1-col"}
                            data={inventoryProducts}
                            renderItem={renderInventoryCard}
                            keyExtractor={(item, index) => (item?.id ? `${item.id}-${index}` : index.toString())}
                            numColumns={isLandscape ? 2 : 1}
                            columnWrapperStyle={isLandscape ? styles.columnWrapper : undefined}
                            showsVerticalScrollIndicator={false}
                            contentContainerStyle={[
                                styles.listContent,
                                !inventoryProducts.length && styles.emptyContent,
                            ]}
                            onMomentumScrollBegin={() => {
                                onMomentumScrollBegin.current = true;
                            }}
                            onEndReached={handleEndReached}
                            onEndReachedThreshold={0.1}
                            refreshControl={
                                <RefreshControl
                                    refreshing={refresh}
                                    onRefresh={handleRefresh}
                                    colors={[theme?.primary ?? appColors.primary]}
                                    tintColor={theme?.primary ?? appColors.primary}
                                />
                            }
                            ListEmptyComponent={
                                <ZenEmptyComponent
                                    text="No inventory products found"
                                    viewStyle={styles.emptyComponentView}
                                />
                            }
                            ListFooterComponent={
                                isFooterLoading ? (
                                    <View style={styles.footerLoader}>
                                        <ActivityIndicator size="small" color={theme?.primary ?? appColors.primary} />
                                    </View>
                                ) : null
                            }
                        />
                    )}

                    {/* Filter Modal */}
                    <ZenFilterModal
                        setVisible={setIsVisibleFilter}
                        visible={isVisibleFilter}
                        filterItems={filterData}
                        setFilterItems={setFilterData}
                        filterCount={filtercount}
                        setFilterCount={setFilterCount}
                        title="Inventory Filter"
                        isSingleExpand={false}
                        onConfirm={(val) => {
                            setLoad(true);
                            if (val?.values?.["Stock Count"]) {
                                setFilterValue({ stockCount: val.values["Stock Count"] });
                            } else {
                                setFilterValue(null);
                            }
                        }}
                    />

                    {/* Update Quantity Modal */}
                    {selectedItem && (
                        <CustomModal visible={isUpdatingQuantity} transparent animationType="slide">
                            <View style={styles.modalOverlay}>
                                <UpdateQuantity
                                    setIsUpdatingQuantity={setIsUpdatingQuantity}
                                    load={load}
                                    setLoad={setLoad}
                                    item={selectedItem}
                                    selectedLocation={locationId}
                                    getAllInventoryProducts={() => fetchInventoryProducts(0, true)}
                                />
                            </View>
                        </CustomModal>
                    )}
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    );
};

export default ProductInventory;

const styles = StyleSheet.create({
    screenContainer: {
        flex: 1,
        backgroundColor: "#F8FAFC",
    },
    loaderContainer: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
    },
    listContent: {
        paddingHorizontal: 12,
        paddingTop: 8,
        paddingBottom: 24,
    },
    emptyContent: {
        flexGrow: 1,
        justifyContent: "center",
        alignItems: "center",
    },
    columnWrapper: {
        columnGap: 12,
    },
    card: {
        backgroundColor: appColors.light,
        borderRadius: 14,
        padding: 14,
        marginVertical: 6,
        borderWidth: 1,
        borderColor: "#E2E8F0",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.04,
        shadowRadius: 6,
        elevation: 2,
    },
    gridCard: {
        flex: 1,
    },
    cardHeader: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },
    titleWrapper: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        columnGap: 8,
        marginRight: 8,
    },
    productTitle: {
        fontFamily: appFonts.bold,
        flexShrink: 1,
    },
    digitalBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    },
    statusBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 8,
    },
    badgeText: {
        fontFamily: appFonts.medium,
        fontSize: fontSize.badgeFontSize,
    },
    divider: {
        height: 1,
        backgroundColor: "#F1F5F9",
        marginVertical: 10,
    },
    cardBody: {
        flexDirection: "row",
        alignItems: "center",
        columnGap: 12,
    },
    thumbnail: {
        width: 66,
        height: 66,
        borderRadius: 10,
        backgroundColor: "#F1F5F9",
        borderWidth: 1,
        borderColor: "#E2E8F0",
    },
    infoCol: {
        flex: 1,
        rowGap: 4,
    },
    metaRow: {
        flexDirection: "row",
        alignItems: "center",
    },
    metaLabel: {
        fontFamily: appFonts.medium,
        marginRight: 4,
    },
    stockRow: {
        flexDirection: "row",
        alignItems: "center",
        columnGap: 6,
    },
    lowStockPill: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: "#FFFBEB",
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 6,
        columnGap: 2,
    },
    editButton: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: "#F0F7FA",
        borderWidth: 1,
        borderColor: "rgba(0, 83, 116, 0.1)",
        justifyContent: "center",
        alignItems: "center",
    },
    emptyComponentView: {
        backgroundColor: "transparent",
        paddingVertical: 50,
    },
    footerLoader: {
        paddingVertical: 16,
        alignItems: "center",
    },
    modalOverlay: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(0,0,0,0.45)",
    },
});